/**
 * Deterministic PRNG (mulberry32). Simulation randomness must be reproducible,
 * so gameplay never calls Math.random() — it goes through a seeded Rng instance.
 * Cosmetic-only randomness (particle jitter) may use its own throwaway instance.
 */
export class Rng {
  private state: number

  constructor(seed = 0x9e3779b9) {
    this.state = seed >>> 0
  }

  /** Uniform in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0
    let t = this.state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  /** Uniform in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min)
  }

  /** Integer in [min, max]. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1))
  }

  /** Uniform in [-spread, spread]. */
  spread(spread: number): number {
    return (this.next() * 2 - 1) * spread
  }

  bool(chance = 0.5): boolean {
    return this.next() < chance
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)]
  }

  angle(): number {
    return this.next() * Math.PI * 2
  }

  reseed(seed: number): void {
    this.state = seed >>> 0
  }
}

/** Shared instance for purely cosmetic jitter (never for simulation). */
export const cosmeticRng = new Rng(0x1a2b3c4d)
